import { c as _c } from "react/compiler-runtime";
import { CLERK_CREDENTIAL_ERROR_NAME } from '@lib/clerk/errors/clerk-credential-error';
import { useClerkSignIn } from '@lib/clerk/hooks/use-clerk-signin';
import { useMutation, useQueryClient } from '@lib/query/client';
export interface SignInCredentials {
  email: string;
  password: string;
}
export interface UseSignInResult {
  signIn: (credentials: SignInCredentials, redirectTo?: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Abstraction layer for user sign-in
 * Delegates to Clerk-specific implementation
 *
 * @returns Object containing:
 *  - signIn: Function to sign in with email and password
 *  - isLoading: Whether sign-in is in progress
 *  - error: Error message if sign-in failed, null otherwise
 *
 * @example
 * ```tsx
 * const { signIn, isLoading, error } = useSignIn();
 *
 * const handleSubmit = async (values) => {
 *   await signIn(
 *     { email: values.email, password: values.password },
 *     '/dashboard'
 *   );
 * };
 * ```
 */
export function useSignIn() {
  const $ = _c(14);
  const queryClient = useQueryClient();
  const {
    signInWithCredentials,
    isLoading,
    error
  } = useClerkSignIn();
  let t0;
  if ($[0] !== signInWithCredentials) {
    t0 = async t1 => {
      const {
        credentials,
        redirectTo
      } = t1;
      await signInWithCredentials(credentials, redirectTo);
    };
    $[0] = signInWithCredentials;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      suppressSentryErrorNames: [CLERK_CREDENTIAL_ERROR_NAME]
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient) {
    t2 = () => {
      queryClient.clear();
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t0 || $[6] !== t2) {
    t3 = {
      mutationFn: t0,
      retry: false,
      meta: t1,
      onSuccess: t2
    };
    $[5] = t0;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const mutation = useMutation(t3);
  let t4;
  if ($[8] !== mutation) {
    t4 = (credentials_0, redirectTo_0) => mutation.mutateAsync({
      credentials: credentials_0,
      redirectTo: redirectTo_0
    });
    $[8] = mutation;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = isLoading || mutation.isPending;
  const t6 = error || (mutation.error instanceof Error ? mutation.error.message : null);
  let t7;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t7 = {
      signIn: t4,
      isLoading: t5,
      error: t6
    };
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}